import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Activity, MousePointerClick, FilePlus2 } from 'lucide-react';
interface SlideFlywheelProps {
  onContinue: () => void;
}
// Slide 6 — The Campaign Learning Curve
// Simple framing: the data we collect on every campaign lifts the next.
// Three concrete outcomes: Engagement, CTA, Rx.
export function SlideFlywheel({
  onContinue
}: SlideFlywheelProps) {
  const [intensity, setIntensity] = useState(2); // 1 = light, 2 = realistic, 3 = ambitious
  // Outcome model — kept deliberately simple.
  // Baseline + lift, shown as plain rates.
  const lifts = {
    1: {
      engagement: 2,
      cta: 1,
      rx: 0.3,
      label: 'Conservative'
    },
    2: {
      engagement: 4,
      cta: 2,
      rx: 0.6,
      label: 'Realistic'
    },
    3: {
      engagement: 8,
      cta: 4,
      rx: 1.2,
      label: 'Ambitious'
    }
  } as const;
  const current = lifts[intensity as 1 | 2 | 3];
  const baselines = [{
    key: 'engagement',
    icon: <Activity className="w-5 h-5" data-id="element-217" />,
    label: 'Engagement rate',
    base: 20,
    lift: current.engagement,
    unit: '%',
    caption: 'HCPs that interact with the campaign'
  }, {
    key: 'cta',
    icon: <MousePointerClick className="w-5 h-5" data-id="element-218" />,
    label: 'CTA conversion',
    base: 12,
    lift: current.cta,
    unit: '%',
    caption: 'Clicks, scans, meeting requests, content opens'
  }, {
    key: 'rx',
    icon: <FilePlus2 className="w-5 h-5" data-id="element-219" />,
    label: 'Rx intent uplift',
    base: 3,
    lift: current.rx,
    unit: '%',
    caption: 'Prescribing intent signalled in follow-up surveys'
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-220">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-221">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-222">
          The Campaign Learning Curve
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-223">
          Every campaign gives us data.{' '}
          <span className="text-teal-400" data-id="element-224">We use it to lift the next one.</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-225">
          Same reach. Same budget. The insights from each campaign — what works,
          where, for whom — feed directly into Engagement, CTA and Rx intent.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10 items-center" data-id="element-226">
        {/* LEFT — Single conceptual control */}
        <div className="lg:col-span-4 space-y-5" data-id="element-227">
          <div className="bg-navy-900/70 p-6 rounded-2xl border border-navy-600 space-y-5" data-id="element-228">
            <div data-id="element-229">
              <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-2" data-id="element-230">
                How aggressively we apply the data
              </p>
              <div className="text-3xl font-serif text-teal-400" data-id="element-231">
                {current.label}
              </div>
            </div>

            <input type="range" min="1" max="3" step="1" value={intensity} onChange={e => setIntensity(Number(e.target.value))} className="w-full accent-teal-400" data-id="element-232" />
            <div className="flex justify-between text-[10px] text-white/40 font-mono" data-id="element-233">
              <span data-id="element-234">Conservative</span>
              <span data-id="element-235">Realistic</span>
              <span data-id="element-236">Ambitious</span>
            </div>
          </div>

          <div className="bg-navy-800/40 border border-navy-700 rounded-xl p-4" data-id="element-237">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-2" data-id="element-238">
              What feeds the loop
            </p>
            <ul className="space-y-1.5 text-white/65 text-xs leading-relaxed" data-id="element-239">
              <li data-id="element-240">
                <span className="text-teal-400 mr-2" data-id="element-241">·</span>Quiz answers reveal
                knowledge gaps
              </li>
              <li data-id="element-242">
                <span className="text-teal-400 mr-2" data-id="element-243">·</span>Country performance
                shapes channel mix
              </li>
              <li data-id="element-244">
                <span className="text-teal-400 mr-2" data-id="element-245">·</span>CTA behaviour
                refines messaging
              </li>
            </ul>
          </div>
        </div>

        {/* RIGHT — Three outcome bars */}
        <div className="lg:col-span-8 space-y-4" data-id="element-246">
          {baselines.map((row, idx) => {
          const newValue = row.base + row.lift;
          const maxScale = row.base * 2; // for bar scaling
          const basePct = row.base / maxScale * 100;
          const newPct = newValue / maxScale * 100;
          return <motion.div key={row.key} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-5" data-id="element-247">
                <div className="flex items-center justify-between mb-3" data-id="element-248">
                  <div className="flex items-center gap-3" data-id="element-249">
                    <div className="w-9 h-9 rounded-lg bg-teal-400/10 border border-teal-400/20 flex items-center justify-center text-teal-400" data-id="element-250">
                      {row.icon}
                    </div>
                    <div data-id="element-251">
                      <div className="text-white font-medium text-sm" data-id="element-252">
                        {row.label}
                      </div>
                      <div className="text-white/40 text-[11px]" data-id="element-253">
                        {row.caption}
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-254">
                    <div className="flex items-baseline gap-2" data-id="element-255">
                      <span className="text-white/40 line-through text-sm" data-id="element-256">
                        {row.base}
                        {row.unit}
                      </span>
                      <motion.span key={newValue} initial={{
                    opacity: 0,
                    y: 4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="text-3xl font-serif text-teal-400" data-id="element-257">
                        {newValue}
                        {row.unit}
                      </motion.span>
                    </div>
                    <div className="text-teal-300/70 text-[10px] uppercase tracking-widest font-medium" data-id="element-258">
                      +{row.lift}
                      {row.unit} lift
                    </div>
                  </div>
                </div>

                {/* Comparison bar */}
                <div className="relative h-2 bg-navy-800 rounded-full overflow-hidden" data-id="element-259">
                  <div className="absolute top-0 left-0 h-full bg-white/15" style={{
                width: `${basePct}%`
              }} data-id="element-260" />
                  <motion.div key={`${row.key}-${newPct}`} initial={{
                width: `${basePct}%`
              }} animate={{
                width: `${newPct}%`
              }} transition={{
                duration: 0.6,
                ease: 'easeOut'
              }} className="absolute top-0 left-0 h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full" data-id="element-261" />
                </div>
              </motion.div>;
        })}

          <p className="text-white/60 text-sm border-l-2 border-teal-400/40 pl-3 py-1" data-id="element-262">
            The first campaign creates engagement.{' '}
            <span className="text-white font-medium" data-id="element-263">
              The next campaigns create advantage —
            </span>{' '}
            from a click, to a conversation, to a script.
          </p>
        </div>
      </div>

      <div className="flex justify-end mt-6" data-id="element-264">
        <Button onClick={onContinue} data-id="element-265">Show CTA lift →</Button>
      </div>
    </div>;
}